import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CartUpdateEvent, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Child component that encapsulates all DMart-specific logic:
 * - Cart fetch / local sync / session tracking
 * - Proceed-to-buy flow (sequential item sync + redirect)
 *
 * The parent (PmPriceDiscoveryComponent) delegates DMart cart operations here.
 */
@Component({
    selector: 'app-dmart-supplier',
    template: '', // DMart has no standalone template; all UI is in the parent
    styles: []
})
export class DmartSupplierComponent implements OnDestroy {

    // ── Inputs from parent ──
    @Input() entityId = '';
    @Input() storeId = '';
    @Input() supplierSection: SupplierSection | null = null;

    // ── Outputs to parent ──
    @Output() cartUpdated = new EventEmitter<CartUpdateEvent>();
    @Output() checkoutDrawerRequested = new EventEmitter<string>(); // platform

    // ── Cart state ──
    apiCartCount = 0;
    apiCartItems: SupplierProduct[] = []; // Normalized items for checkout display
    // Maps ALL possible DMart IDs (productId, skuUniqueID, articleNumber) → quantity
    cartQuantityMap = new Map<string, number>();
    // Maps any DMart ID variant → canonical ID (productId)
    idToCanonicalId = new Map<string, string>();

    // ── Session tracking ──
    sessionModifiedSkus = new Set<string>();
    private originalQuantities = new Map<string, number>();
    isDmartSessionItemsCollapsed = false;
    isDmartCartItemsCollapsed = true;

    private destroy$ = new Subject<void>();

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Public API for Parent ──

    /**
     * Called by parent after integration status is resolved.
     */
    initialize(): void {
        if (this.storeId && this.entityId) {
            this.loadSessionModifiedSkus();
            this.fetchCart();
        }
    }

    /**
     * Called by parent after entity changes.
     */
    resetState(): void {
        this.apiCartCount = 0;
        this.apiCartItems = [];
        this.cartQuantityMap.clear();
        this.idToCanonicalId.clear();
        this.clearSessionModifiedSkus();
    }

    /**
     * Updates an item quantity and syncs with DMart API.
     */
    updateQuantity(item: SupplierProduct, change: number, previousQuantity: number): void {
        // Optimistically sync local cart state
        this.syncCartLocally(String(item.sku_id));

        item.isAddingToCart = true;

        this.priceDiscoveryService.addToDmartCart(
            String(item.sku_id),
            item.selectedQuantity || 0,
            this.entityId
        ).subscribe({
            next: (response) => {
                console.log('✅ DMart cart updated:', response);
                item.isAddingToCart = false;
                if (change > 0 && previousQuantity === 0) {
                    this.sharedService.successAlert(`Added "${item.item_name}" to cart`);
                }
            },
            error: (error) => {
                console.error('❌ Failed to update DMart cart:', error);
                item.isAddingToCart = false;
                item.selectedQuantity = previousQuantity;
                this.syncCartLocally(String(item.sku_id));
                const errorMessage = error?.error?.message || error?.message || 'Failed to update cart. Please try again.';
                item.cartError = errorMessage;
                this.sharedService.errorAlert(errorMessage);
            }
        });
    }

    /**
     * Adds an item to the cart (first time add).
     */
    addToCart(item: SupplierProduct, supplierName: string, previousQuantity: number): void {
        // Optimistically sync local cart state
        this.syncCartLocally(String(item.sku_id));

        item.isAddingToCart = true;

        this.priceDiscoveryService.addToDmartCart(
            String(item.sku_id),
            item.selectedQuantity || 1,
            this.entityId
        ).subscribe({
            next: (response) => {
                console.log('✅ DMart item added to cart:', response);
                item.isAddingToCart = false;
                this.sharedService.successAlert(`Added "${item.item_name}" from ${supplierName} to cart`);
            },
            error: (error) => {
                console.error('❌ Failed to add DMart item to cart:', error);
                item.isAddingToCart = false;
                item.selectedQuantity = previousQuantity;
                this.syncCartLocally(String(item.sku_id));
                const errorMessage = error?.error?.message || error?.message || 'Failed to add item to cart. Please try again.';
                item.cartError = errorMessage;
                this.sharedService.errorAlert(errorMessage);
            }
        });
    }

    /**
     * Proceed-to-buy flow: redirect to DMart cart page.
     *
     * NOTE: We intentionally do NOT clear local cart state here.
     * Items are already synced to DMart's backend in real-time (via addToDmartCart API
     * on every qty change). After the user completes (or abandons) checkout on dmart.in
     * and returns to the app, fetchCart() will re-fetch the true cart state from DMart's
     * API and reconcile all local state (apiCartItems, apiCartCount, selectedQuantity,
     * sessionModifiedSkus, localStorage.dmartCart) automatically.
     *
     * Clearing local state here would only cause a brief inconsistency if the user does
     * NOT complete the order on dmart.in (cart would appear empty before fetchCart runs).
     */
    proceedToBuy(checkoutItems: SupplierProduct[]): void {
        if (!checkoutItems || checkoutItems.length === 0) return;

        console.log(`🛒 Redirecting to DMart with ${checkoutItems.length} items in cart...`);

        // Open DMart cart page directly — items are already synced server-side
        window.open('https://www.dmart.in/cart', '_blank');
        this.sharedService.successAlert('Opening DMart website...');
    }

    /**
     * Returns session-modified items from a checkout cart.
     */
    getSessionModifiedItems(checkoutItems: SupplierProduct[]): SupplierProduct[] {
        return (checkoutItems || []).filter(
            (item: SupplierProduct) => {
                const canonical = this.idToCanonicalId.get(String(item.sku_id)) || String(item.sku_id);
                return this.sessionModifiedSkus.has(canonical);
            }
        );
    }

    /**
     * Returns non-session (pre-existing) items from a checkout cart.
     */
    getNonSessionCartItems(checkoutItems: SupplierProduct[]): SupplierProduct[] {
        return (checkoutItems || []).filter(
            (item: SupplierProduct) => {
                const canonical = this.idToCanonicalId.get(String(item.sku_id)) || String(item.sku_id);
                return !this.sessionModifiedSkus.has(canonical);
            }
        );
    }

    // ── Cart Operations ──

    fetchCart(): void {
        if (!this.storeId || !this.entityId) return;

        this.priceDiscoveryService.getDmartCart(this.storeId, this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const data = response?.response || response?.data || response || {};
                    const products = data.products || [];
                    const cartTotalItem = Number(data.cartTotalItem || 0) || products.length;

                    if (cartTotalItem === 0 || products.length === 0) {
                        this.clearSessionModifiedSkus();
                        this.apiCartCount = 0;
                        this.apiCartItems = [];
                        this.cartQuantityMap.clear();
                        this.idToCanonicalId.clear();
                        this.emitCartUpdate();
                        return;
                    }

                    this.apiCartCount = cartTotalItem;

                    // Flatten products → sKUs into normalized SupplierProduct items
                    const normalizedItems: SupplierProduct[] = [];
                    products.forEach((product: any) => {
                        const skus = product.sKUs || product.skus || product.SKUs || [];
                        skus.forEach((sku: any) => {
                            const rate = Number(sku.priceSALE || sku.priceSale || sku.price || 0);
                            const mrp = Number(sku.priceMRP || sku.priceMrp || sku.mrp || 0);
                            const qty = Number(sku.addedQuantity || sku.quantity || 1);
                            const maxQty = Number(sku.maxQuantity || 0) || undefined;

                            normalizedItems.push({
                                item_name: sku.name || product.name || '',
                                sku_id: String(product.productId || sku.skuUniqueID || ''),
                                rate: rate,
                                baseRate: rate,
                                mrp: mrp,
                                price: rate,
                                unit: sku.variantTextValue || 'unit',
                                quantity: qty,
                                image_url: '',
                                product_images: '',
                                platform: 'dmart',
                                isBestMatch: false,
                                is_in_stock: sku.invStatus !== '0',
                                bulkPrices: [],
                                selectedQuantity: qty,
                                minimumOrderQty: undefined,
                                maximumOrderQty: maxQty
                            } as SupplierProduct);
                        });
                    });

                    this.apiCartItems = normalizedItems;

                    // Save to localStorage
                    try {
                        const cartData = normalizedItems.map(item => ({
                            productId: item.sku_id,
                            productName: item.item_name,
                            quantity: item.selectedQuantity
                        }));
                        localStorage.setItem('dmartCart', JSON.stringify(cartData));
                    } catch (error) {
                        console.error('Failed to save DMart cart to localStorage:', error);
                    }

                    // Build multi-key map
                    this.cartQuantityMap.clear();
                    this.idToCanonicalId.clear();
                    products.forEach((prod: any) => {
                        const skus = prod.sKUs || prod.skus || prod.SKUs || [];
                        skus.forEach((sk: any) => {
                            const qty = Number(sk.addedQuantity || sk.quantity || 1);
                            const pId = String(prod.productId || '');
                            const skuUId = String(sk.skuUniqueID || sk.skuUniqueId || '');
                            const artNum = String(sk.articleNumber || '');
                            const canonical = pId || skuUId || artNum;
                            if (pId) {
                                this.cartQuantityMap.set(pId, qty);
                                this.idToCanonicalId.set(pId, canonical);
                            }
                            if (skuUId) {
                                this.cartQuantityMap.set(skuUId, qty);
                                this.idToCanonicalId.set(skuUId, canonical);
                            }
                            if (artNum) {
                                this.cartQuantityMap.set(artNum, qty);
                                this.idToCanonicalId.set(artNum, canonical);
                            }
                        });
                    });

                    // Migrate session data to canonical IDs
                    const migratedSkus = new Set<string>();
                    this.sessionModifiedSkus.forEach(id => {
                        const canonical = this.idToCanonicalId.get(id) || id;
                        migratedSkus.add(canonical);
                    });
                    this.sessionModifiedSkus = migratedSkus;

                    const migratedOriginals = new Map<string, number>();
                    this.originalQuantities.forEach((qty, id) => {
                        const canonical = this.idToCanonicalId.get(id) || id;
                        if (!migratedOriginals.has(canonical)) {
                            migratedOriginals.set(canonical, qty);
                        }
                    });
                    this.originalQuantities = migratedOriginals;

                    // Snapshot original quantities
                    normalizedItems.forEach((item: SupplierProduct) => {
                        const canonical = this.idToCanonicalId.get(String(item.sku_id)) || String(item.sku_id);
                        if (!this.originalQuantities.has(canonical)) {
                            this.originalQuantities.set(canonical, item.selectedQuantity || 0);
                        }
                    });
                    this.saveSessionModifiedSkus();

                    // Update selectedQuantity on matching items in the supplier section
                    if (this.supplierSection) {
                        this.supplierSection.items.forEach(item => {
                            const skuId = String(item.sku_id || '');
                            if (this.cartQuantityMap.has(skuId)) {
                                item.selectedQuantity = this.cartQuantityMap.get(skuId) || 0;
                            }
                        });
                    }

                    // Notify parent
                    this.emitCartUpdate();
                },
                (error: any) => {
                    console.error('❌ Failed to fetch DMart cart:', error);
                }
            );
    }

    syncCartLocally(modifiedSkuId?: string): void {
        const searchItems = this.supplierSection?.items || [];

        const activeCanonicalIds = new Set<string>();
        searchItems.forEach(item => {
            const skuStr = String(item.sku_id);
            if (item.selectedQuantity && item.selectedQuantity > 0) {
                const canonical = this.idToCanonicalId.get(skuStr) || skuStr;
                activeCanonicalIds.add(canonical);
            }
        });
        if (modifiedSkuId) {
            const canonical = this.idToCanonicalId.get(String(modifiedSkuId)) || String(modifiedSkuId);
            activeCanonicalIds.add(canonical);
        }

        const unchangedApiItems = this.apiCartItems.filter(ai => {
            const aiCanonical = this.idToCanonicalId.get(String(ai.sku_id)) || String(ai.sku_id);
            return !activeCanonicalIds.has(aiCanonical);
        });

        const searchItemsInCart = searchItems.filter(
            item => item.selectedQuantity && item.selectedQuantity > 0
        );

        this.apiCartItems = [
            ...unchangedApiItems,
            ...searchItemsInCart.map(item => ({
                item_name: item.item_name,
                sku_id: item.sku_id,
                rate: item.rate,
                baseRate: item.baseRate || item.rate,
                mrp: item.mrp || 0,
                price: item.price || 0,
                unit: item.unit,
                quantity: item.selectedQuantity || 1,
                image_url: item.image_url || '',
                product_images: item.product_images || '',
                platform: 'dmart',
                isBestMatch: item.isBestMatch || false,
                is_in_stock: item.is_in_stock !== false,
                bulkPrices: item.bulkPrices || [],
                selectedQuantity: item.selectedQuantity || 1,
                minimumOrderQty: item.minimumOrderQty,
                maximumOrderQty: item.maximumOrderQty
            } as SupplierProduct))
        ];

        this.apiCartCount = this.apiCartItems.length;

        // Save to localStorage
        try {
            const cartData = this.apiCartItems.map(item => ({
                product_id: item.sku_id,
                product_name: item.item_name,
                quantity: item.selectedQuantity
            }));
            localStorage.setItem('dmartCart', JSON.stringify(cartData));
        } catch (error) {
            console.error('Failed to save DMart cart to localStorage:', error);
        }

        // Track the modified SKU
        if (modifiedSkuId) {
            const modifiedCanonical = this.idToCanonicalId.get(String(modifiedSkuId)) || String(modifiedSkuId);
            const modifiedItem = this.apiCartItems.find(i => {
                const c = this.idToCanonicalId.get(String(i.sku_id)) || String(i.sku_id);
                return c === modifiedCanonical;
            });
            const currentQty = modifiedItem ? (modifiedItem.selectedQuantity || 0) : 0;
            this.trackSessionModification(modifiedSkuId, currentQty);
        }

        // Notify parent
        this.emitCartUpdate();
    }

    // ── Session Tracking ──

    private trackSessionModification(skuId: string, currentQty: number): void {
        const canonical = this.idToCanonicalId.get(skuId) || skuId;
        const originalQty = this.originalQuantities.get(canonical) ?? 0;
        if (currentQty !== originalQty) {
            this.sessionModifiedSkus.add(canonical);
        } else {
            this.sessionModifiedSkus.delete(canonical);
        }
        this.saveSessionModifiedSkus();
    }

    private loadSessionModifiedSkus(): void {
        try {
            const stored = localStorage.getItem('dmartSessionItems');
            if (stored) {
                const data = JSON.parse(stored);
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    this.sessionModifiedSkus = new Set(data.skus || []);
                    this.originalQuantities = new Map(
                        Object.entries(data.originals || {}).map(([k, v]) => [k, Number(v)])
                    );
                } else if (Array.isArray(data)) {
                    this.sessionModifiedSkus = new Set(data);
                    this.originalQuantities = new Map();
                }
            }
        } catch (e) {
            this.sessionModifiedSkus = new Set();
            this.originalQuantities = new Map();
        }
    }

    private saveSessionModifiedSkus(): void {
        try {
            const originals: { [key: string]: number } = {};
            this.originalQuantities.forEach((v, k) => originals[k] = v);
            const data = {
                skus: [...this.sessionModifiedSkus],
                originals
            };
            localStorage.setItem('dmartSessionItems', JSON.stringify(data));
        } catch (e) {
            console.error('Failed to save DMart session items:', e);
        }
    }

    clearSessionModifiedSkus(): void {
        this.sessionModifiedSkus.clear();
        this.originalQuantities.clear();
        localStorage.removeItem('dmartSessionItems');
    }

    private emitCartUpdate(): void {
        this.cartUpdated.emit({
            platform: 'dmart',
            cartCount: this.apiCartCount,
            cartItems: this.apiCartItems
        });
    }
}
